import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import LoginModal from './LoginModal';
import RegisterModal from './RegisterModal';
import DoctorRegisterModal from './DoctorRegisterModal';
import '../styles/Header.css'

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loggedInUser, setLoggedInUser] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('DDoctorUser'));
    if (storedUser) {
        setLoggedInUser(storedUser);
    }
  }, [location]);

  const handleLogout = () => {
    setLoggedInUser(null);
    localStorage.removeItem('DDoctorUser');
    navigate('/');
  };

  // --- 1. NEW: Helper to close menu on click ---
  const handleNavClick = () => {
    const navbar = document.getElementById("navbarSupportedContent");
    if (navbar && navbar.classList.contains("show")) {
      navbar.classList.remove("show");
    }
  };

  // --- Helper to format name based on role ---
  const getDisplayName = () => {
    if (!loggedInUser) return '';

    if (loggedInUser.role === 'doctor') {
      const nameLower = loggedInUser.name.toLowerCase();
      if (nameLower.startsWith('dr.') || nameLower.startsWith('dr ')) {
        return loggedInUser.name;
      }
      return `Dr. ${loggedInUser.name}`;
    }

    return loggedInUser.name;
  };

  const navLinkStyle = { fontSize: "22px", fontFamily: "'Times New Roman', Times, sans-serif", color: "black", fontWeight: "500", whiteSpace: "nowrap" };
  const avatarStyle = { width: '45px', height: '45px', backgroundColor: '#E6E6FA', color: '#6A5ACD', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background 0.3s' };

  // --- 2. REUSABLE PROFILE DROPDOWN (Used for Mobile & Desktop) ---
  const renderProfileDropdown = (isMobile) => (
    <div className={`dropdown ${isMobile ? 'd-lg-none ms-auto me-2' : 'd-none d-lg-block ms-lg-3'}`}>
      <div className="d-flex align-items-center gap-2" id={`userProfileDropdown${isMobile ? 'Mobile' : 'Desktop'}`} data-bs-toggle="dropdown" aria-expanded="false" style={{ cursor: 'pointer' }}>
        
        {/* Name Display */}
        <span className="fw-bold text-dark me-1" style={{ fontSize: '16px', textTransform: 'capitalize' }}>
          {/* On mobile, splits name to save space, remove .split if you want full name */}
          {isMobile ? getDisplayName().split(' ')[0] : getDisplayName()}
        </span>

        {/* Avatar */}
        <div style={avatarStyle}><i className="bi bi-person-fill fs-4"></i></div>
        <i className="bi bi-chevron-down text-secondary" style={{ fontSize: '12px' }}></i>
      </div>

      <ul className={`dropdown-menu ${isMobile ? 'dropdown-menu-end' : 'dropdown-menu-end'}`} aria-labelledby={`userProfileDropdown${isMobile ? 'Mobile' : 'Desktop'}`}>
        
        {/* A. HOSPITAL */}
        {loggedInUser.role === 'hospital' && (
          <li>
            <Link className="dropdown-item" to="/hospital-dashboard" onClick={handleNavClick}>
              <i className="bi bi-speedometer2 me-2"></i> Hospital Dashboard
            </Link>
          </li>
        )}

        {/* B. DOCTOR */}
        {loggedInUser.role === 'doctor' && (
          <>
            <li>
              <Link className="dropdown-item" to="/doctor-dashboard" onClick={handleNavClick}>
                <i className="bi bi-activity me-2"></i> Doctor Dashboard
              </Link>
            </li>
            <li>
              <Link className="dropdown-item" to="/my-patients" onClick={handleNavClick}>
                <i className="bi bi-people me-2"></i> My Patients
              </Link>
            </li>
          </>
        )}

        {/* C. PATIENT */}
        {loggedInUser.role !== 'hospital' && loggedInUser.role !== 'doctor' && (
          <>
            <li>
              <Link className="dropdown-item" to="/profile" onClick={handleNavClick}>
                <i className="bi bi-person me-2"></i> Patient Dashboard
              </Link>
            </li>
            <li>
              <Link className="dropdown-item" to="/my-appointments" onClick={handleNavClick}>
                <i className="bi bi-calendar-check me-2"></i> My Appointments
              </Link>
            </li>
            <li>
              <Link className="dropdown-item" to="/discount-card" onClick={handleNavClick}>
                <i className="bi bi-credit-card me-2"></i> Discount Card
              </Link>
            </li>
          </>
        )}

        <li><hr className="dropdown-divider" /></li>

        <li>
          <button className="dropdown-item text-danger fw-bold" onClick={() => { handleLogout(); handleNavClick(); }}>
            <i className="bi bi-box-arrow-right me-2"></i> Logout
          </button>
        </li>
      </ul>
    </div>
  );

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm fixed-top">
        <div className="container-fluid">
          
          {/* Brand Logo */}
          <Link className="navbar-brand ms-2" to="/" onClick={handleNavClick}>
            <img src="/images/logo.jpeg" alt="logo" style={{ width: "100px" }} />
          </Link>

          {/* --- 3. MOBILE PROFILE (Visible OUTSIDE the collapse) --- */}
          {loggedInUser && renderProfileDropdown(true)}

          {/* Toggler Button */}
          <button className="navbar-toggler shadow-none border-0" type="button" data-bs-toggle="collapse" data-bs-target="#navbarSupportedContent">
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse" id="navbarSupportedContent">

            <ul className="navbar-nav ms-lg-4 me-auto mb-2 mb-lg-0" style={{ paddingTop: "10px", paddingBottom: "10px" }}>
              
              <li className="nav-item dropdown me-2">
                <a className="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown" style={navLinkStyle}>Doctors</a>
                <ul className="dropdown-menu" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <li><Link className="dropdown-item" to="/doctors" onClick={handleNavClick}>Find doctor by speciality</Link></li>
                  <li><hr className="dropdown-divider" /></li> 
                  <li><Link className="dropdown-item" to="/specialty/dermatologist" onClick={handleNavClick}>Dermatologist</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/gynecologist" onClick={handleNavClick}>Gynecologist</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/urologist" onClick={handleNavClick}>Urologist</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/gastroenterologist" onClick={handleNavClick}>Gastroenterologist</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/neurologist" onClick={handleNavClick}>Neurologist</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/ent-specialist" onClick={handleNavClick}>Ent Specialist</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/psychiatrist" onClick={handleNavClick}>Psychiatrist</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/pediatrician" onClick={handleNavClick}>Pediatrician</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/dentist" onClick={handleNavClick}>Dentist</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/sexologist" onClick={handleNavClick}>Sexologist</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/laser-hair-removal" onClick={handleNavClick}>Laser Hair Removal</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/hydrafacial" onClick={handleNavClick}>Hydrafacial</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/mri" onClick={handleNavClick}>MRI</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/ct-scan" onClick={handleNavClick}>CT Scan</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/piles" onClick={handleNavClick}>Piles</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/hernia" onClick={handleNavClick}>Hernia</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/erectile-dysfunction" onClick={handleNavClick}>Erectile Dysfunction</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/stomach-pain" onClick={handleNavClick}>Stomach Pain</Link></li>
                  <li><Link className="dropdown-item" to="/specialty/cardiology" onClick={handleNavClick}>Cardiology</Link></li>
                </ul>
              </li>

              <li className="nav-item dropdown me-2">
                <a className="nav-link dropdown-toggle" href="#" id="hospitalsDropdown" role="button" data-bs-toggle="dropdown" style={navLinkStyle}>
                  Hospitals
                </a>
                <ul className="dropdown-menu">
                  <li><Link className="dropdown-item" to="/hospitals/skardu" onClick={handleNavClick}>Skardu Hospitals</Link></li>
                  <li><Link className="dropdown-item" to="/hospitals/ghanche" onClick={handleNavClick}>Ghanche Hospitals</Link></li>
                  <li><Link className="dropdown-item" to="/hospitals/shigar" onClick={handleNavClick}>Shigar Hospitals</Link></li>
                  <li><Link className="dropdown-item" to="/hospitals/kharmang" onClick={handleNavClick}>Kharmang Hospitals</Link></li>
                  <li><Link className="dropdown-item" to="/hospitals/roundu" onClick={handleNavClick}>Roundu Hospitals</Link></li>
                </ul>
              </li>

              <li className="nav-item dropdown me-2">
                <a className="nav-link dropdown-toggle" href="#" id="labsDropdown" role="button" data-bs-toggle="dropdown" style={navLinkStyle}>
                  Labs and Diagnostic
                </a>
                <ul className="dropdown-menu">
                  <li><Link className="dropdown-item" to="/labs/skardu" onClick={handleNavClick}>Skardu Labs</Link></li>
                  <li><Link className="dropdown-item" to="/labs/ghanche" onClick={handleNavClick}>Ghanche Labs</Link></li>
                  <li><Link className="dropdown-item" to="/labs/shigar" onClick={handleNavClick}>Shigar Labs</Link></li>
                  <li><Link className="dropdown-item" to="/labs/kharmang" onClick={handleNavClick}>Kharmang Labs</Link></li>
                  <li><Link className="dropdown-item" to="/labs/roundu" onClick={handleNavClick}>Roundu Labs</Link></li>
                </ul>
              </li>

              <li className="nav-item me-2"><Link className="nav-link" to="/surgeries" style={navLinkStyle} onClick={handleNavClick}>Surgeries</Link></li>
              <li className="nav-item me-2"><Link className="nav-link" to="/health" style={navLinkStyle} onClick={handleNavClick}>Health Blog</Link></li>
              <li className="nav-item me-2"><Link className="nav-link" to="/reviews" style={navLinkStyle} onClick={handleNavClick}>Reviews</Link></li>
            </ul>

            <div className="d-flex align-items-center">

              {!loggedInUser && (
                <button
                  type="button"
                  className="btn text-black fw-bold shadow-none me-3"
                  style={{ backgroundColor: '#ffc107', borderRadius: '5px',whiteSpace: 'nowrap' }}
                  data-bs-toggle="modal"
                  data-bs-target="#doctorRegisterModal"
                  onClick={handleNavClick} // Close menu if open
                >
                  join as Doctor
                </button>
              )}

              {/* --- 4. DESKTOP PROFILE (Visible inside menu on desktop) --- */}
              {loggedInUser ? (
                renderProfileDropdown(false)
              ) : (
                <>
                  <button type="button" className="btn btn-outline-dark shadow-none me-2" data-bs-toggle="modal" data-bs-target="#loginModal" onClick={handleNavClick}>Login</button>
                  <button type="button" className="btn btn-outline-dark shadow-none" data-bs-toggle="modal" data-bs-target="#registerModal" onClick={handleNavClick}>Register</button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <LoginModal setLoggedInUser={setLoggedInUser} />
      <RegisterModal />
      <DoctorRegisterModal />

    </>
  );
}

export default Header;